/**
 * Centralized fetch wrapper with global 401 handling.
 *
 * On any 401 response from an API call:
 *   1. Clear auth tokens from localStorage
 *   2. Redirect to /auth/login?reason=expired
 *   3. Reject the promise so calling code can short-circuit cleanly
 *
 * Skipped when the user is already on /auth/* (avoids redirect loops during
 * the login flow itself, where 401 from /api/auth/login is expected and means
 * "bad credentials", not "session expired").
 *
 * To adopt: in any service, replace `fetch(...)` with `apiFetch(...)`.
 * Same signature, same return type — no other changes needed.
 */

const TOKEN_KEYS = ['token', 'refreshToken', 'user'];

export async function apiFetch(input: RequestInfo, init?: RequestInit): Promise<Response> {
  const response = await fetch(input, init);

  if (response.status === 401 && !window.location.pathname.startsWith('/auth/')) {
    TOKEN_KEYS.forEach(k => localStorage.removeItem(k));
    // Use replace() so the expired URL doesn't sit in history — back-button
    // from /auth/login goes to wherever the user was before, not the dead page.
    window.location.replace('/auth/login?reason=expired');
    throw new Error('Session expired');
  }

  return response;
}
